import { useLayoutEffect, useRef, type RefObject } from 'react';

/** Attribute every column root carries so the board can find and measure it. */
export const KANBAN_COLUMN_ATTRIBUTE = 'data-kanban-column';

const FLIP_DURATION_MS = 240;
const FLIP_EASING = 'cubic-bezier(0.2, 0, 0, 1)';

function prefersReducedMotion(): boolean {
    return (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
}

interface ColumnPosition {
    readonly node: HTMLElement;
    readonly left: number;
}

/** Each column's element and left edge inside the scroller, scroll-independent. */
function measure(container: HTMLElement): Map<string, ColumnPosition> {
    const origin = container.getBoundingClientRect().left - container.scrollLeft;
    const positions = new Map<string, ColumnPosition>();
    container.querySelectorAll<HTMLElement>(`[${KANBAN_COLUMN_ATTRIBUTE}]`).forEach((node) => {
        const id = node.getAttribute(KANBAN_COLUMN_ATTRIBUTE);
        if (id) positions.set(id, { node, left: node.getBoundingClientRect().left - origin });
    });
    return positions;
}

/**
 * Slides columns to their new place when the column ORDER changes (Move left /
 * Move right, a reorder from elsewhere) instead of letting them jump — the FLIP
 * technique on the Web Animations API, no animation library.
 *
 * A drag-and-drop reorder needs nothing extra: positions are read as rendered
 * (transforms included), and by the drop the dragged columns already sit where
 * the new order puts them, so their offset is ~0 and nothing animates twice.
 * Skipped for `prefers-reduced-motion` and where `Element.animate` is missing.
 */
export function useColumnFlip(containerRef: RefObject<HTMLElement | null>, orderKey: string) {
    const positionsRef = useRef<Map<string, ColumnPosition>>(new Map());
    const orderRef = useRef(orderKey);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const next = measure(container);
        const isReordered = orderRef.current !== orderKey;
        orderRef.current = orderKey;
        if (isReordered && !prefersReducedMotion()) {
            for (const [id, { node, left }] of next) {
                const before = positionsRef.current.get(id)?.left;
                const offset = before === undefined ? 0 : before - left;
                if (Math.abs(offset) < 1 || typeof node.animate !== 'function') continue;
                node.animate(
                    [{ transform: `translateX(${offset}px)` }, { transform: 'translateX(0)' }],
                    { duration: FLIP_DURATION_MS, easing: FLIP_EASING },
                );
            }
        }
        positionsRef.current = next;
    });
}
